import { useEffect, useRef } from "react";

import { breakpoints } from "../../../../design-tokens/dimensions";
import { thumbnailWidths } from "../../constants";
import { NavButton } from "./NavButton/NavButton";
import { Root, ScrollableContent } from "./styles";
import { Thumbnail } from "./Thumbnail/Thumbnail";

type ThumbnailScrollbarProps = {
  moveLeft: () => void;
  moveRight: () => void;
  currentIndex: number;
  updateCurrentIndex: (value: number) => void;
  thumbnailItems: {
    src: string;
    alt: string;
  }[];
};

export function getScrollAmount(currentBreakpoint: number) {
  let scrollAmount;
  if (currentBreakpoint >= breakpoints.medium) {
    scrollAmount = thumbnailWidths.medium;
  } else if (currentBreakpoint >= breakpoints.small) {
    scrollAmount = thumbnailWidths.small;
  } else if (currentBreakpoint >= breakpoints.xsmall) {
    scrollAmount = thumbnailWidths.xsmall;
  } else {
    scrollAmount = thumbnailWidths.base;
  }
  return scrollAmount;
}

export function ThumbnailScrollbar({
  moveLeft,
  moveRight,
  currentIndex,
  thumbnailItems,
  updateCurrentIndex,
}: ThumbnailScrollbarProps) {
  // Handles the scroll position of the thumbnail scroller whenever the active main view item changes
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    const currentBreakpoint = window.innerWidth;

    scrollRef.current.scrollLeft = currentIndex * getScrollAmount(currentBreakpoint);
  }, [currentIndex]);

  return (
    <Root role="group" aria-label="Choose slide to display">
      <NavButton
        onClick={moveLeft}
        iconVariant="left"
        aria-label="Move carousel left"
        disabled={currentIndex === 0}
      />
      <ScrollableContent ref={scrollRef}>
        {thumbnailItems.map(({ alt, src }, i) => (
          <Thumbnail
            aria-label={`Thumbnail for slide ${i + 1} of ${thumbnailItems.length}`}
            key={`${Thumbnail.name}${i}`}
            isActive={currentIndex === i}
            onClick={() => updateCurrentIndex(i)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                updateCurrentIndex(i);
              }
            }}
          >
            <Thumbnail.Image alt={alt} isActive={currentIndex === i} src={src} />
          </Thumbnail>
        ))}
      </ScrollableContent>
      <NavButton
        onClick={moveRight}
        iconVariant="right"
        aria-label="Move carousel right"
        disabled={currentIndex === thumbnailItems.length - 1}
      />
    </Root>
  );
}
